import React, { useEffect, useState } from "react";
import { BiSend, BiX, BiSearch, BiChevronDown, BiChevronUp } from "react-icons/bi";
import { AiOutlineCheckCircle, AiOutlineCloseCircle } from "react-icons/ai";

export default function ResendCampaignModal({ campaign, onClose }) {
  const [filter, setFilter] = useState("all");
  const [buttonId, setButtonId] = useState("");
  const [contacts, setContacts] = useState([]);
  const [buttons, setButtons] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [previewCount, setPreviewCount] = useState(0);
  const [immediate, setImmediate] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedContacts, setExpandedContacts] = useState(false);

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line
  }, [filter, buttonId]);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      let q = `${process.env.REACT_APP_API_URL}/api/campaigns/${campaign._id}/contacts?filter=${filter}`;
      if (filter === "button" && buttonId) q += `&buttonId=${buttonId}`;
      
      const res = await fetch(q, {
        headers: { "x-email": localStorage.getItem("tenantEmail") || "" },
      });
      const data = await res.json();
      setContacts(data.contacts || []);
      setButtons(data.buttons || []);
      setPreviewCount(data.counts?.filtered || data.contacts?.length || 0);
      setSelected(new Set());
    } catch (err) {
      console.error("Failed to fetch contacts:", err);
      alert("Failed to load contacts data");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (contact) => {
    const newSelected = new Set(selected);
    if (newSelected.has(contact)) {
      newSelected.delete(contact);
    } else {
      newSelected.add(contact);
    }
    setSelected(newSelected);
  };

  const selectAll = () => {
    const filteredContacts = getFilteredContacts();
    const s = new Set(filteredContacts.map((c) => c.mobileNumber));
    setSelected(s);
  };

  const deselectAll = () => setSelected(new Set());

  const getFilteredContacts = () => {
    if (!searchTerm) return contacts;
    return contacts.filter(contact =>
      contact.mobileNumber.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleResend = async () => {
    if (filter === "selected" && selected.size === 0) {
      alert("Please select at least one contact");
      return;
    }

    const payload = {
      rule: filter,
      buttonId: filter === "button" ? buttonId : undefined,
      selectedContacts: filter === "selected" ? Array.from(selected) : undefined,
      immediate,
    };

    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/campaigns/${campaign._id}/resend`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-email": localStorage.getItem("tenantEmail") || "",
          },
          body: JSON.stringify(payload),
        }
      );
      
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to create resend campaign");
      }
      
      alert(`✅ Resend campaign created successfully!\n${data.count} contacts will receive the message.`);
      onClose();
    } catch (err) {
      console.error("Resend failed:", err);
      alert(err.message || "Failed to create resend campaign");
    } finally {
      setLoading(false);
    }
  };

  const getFilterDescription = () => {
    switch (filter) {
      case "all":
        return "All contacts from the original campaign";
      case "clicked":
        return "Contacts who clicked at least one button";
      case "notClicked":
        return "Contacts who didn't click any buttons";
      case "button":
        return buttonId ? `Contacts who clicked Button ${buttonId}` : "Select a button to filter contacts";
      case "selected":
        return "Manually selected contacts";
      default:
        return "";
    }
  };

  const filteredContacts = getFilteredContacts();
  const displayedContacts = expandedContacts ? filteredContacts : filteredContacts.slice(0, 10);

  return (
    <div className="rcm-modal-overlay" onClick={onClose}>
      <div className="rcm-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="rcm-modal-header">
          <div className="rcm-modal-title-section">
            <h4>
              <BiSend className="me-2" />
              Resend Campaign
            </h4>
            <p className="rcm-modal-subtitle">
              Resend "{campaign.name}" to specific contacts
            </p>
          </div>
          <button className="rcm-btn-close" onClick={onClose}>
            <BiX />
          </button>
        </div>

        <div className="rcm-modal-body">
          {/* Campaign Info */}
          <div className="rcm-campaign-info-card">
            <div className="rcm-info-row">
              <span className="rcm-info-label">Template:</span>
              <span className="rcm-info-value">{campaign.templateId}</span>
            </div>
            <div className="rcm-info-row">
              <span className="rcm-info-label">Original Contacts:</span>
              <span className="rcm-info-value">{campaign.contacts?.length || 0}</span>
            </div>
          </div>

          {/* Filter Section */}
          <div className="rcm-filter-section">
            <label className="form-label">Send To</label>
            <div className="rcm-filter-options">
              <label className="rcm-filter-option">
                <input
                  type="radio"
                  name="filter"
                  value="all"
                  checked={filter === "all"}
                  onChange={(e) => setFilter(e.target.value)}
                />
                <span className="rcm-filter-label">All Contacts</span>
              </label>
              
              <label className="rcm-filter-option">
                <input
                  type="radio"
                  name="filter"
                  value="clicked"
                  checked={filter === "clicked"}
                  onChange={(e) => setFilter(e.target.value)}
                />
                <span className="rcm-filter-label">Clicked Any Button</span>
              </label>
              
              <label className="rcm-filter-option">
                <input
                  type="radio"
                  name="filter"
                  value="notClicked"
                  checked={filter === "notClicked"}
                  onChange={(e) => setFilter(e.target.value)}
                />
                <span className="rcm-filter-label">Not Clicked</span>
              </label>
              
              <label className="rcm-filter-option">
                <input
                  type="radio"
                  name="filter"
                  value="button"
                  checked={filter === "button"}
                  onChange={(e) => setFilter(e.target.value)}
                />
                <span className="rcm-filter-label">By Button</span>
              </label>
              
              <label className="rcm-filter-option">
                <input
                  type="radio"
                  name="filter"
                  value="selected"
                  checked={filter === "selected"}
                  onChange={(e) => setFilter(e.target.value)}
                />
                <span className="rcm-filter-label">Select Manually</span>
              </label>
            </div>

            {filter === "button" && (
              <div className="rcm-button-selector">
                <label className="form-label">Select Button</label>
                <select
                  className="form-select"
                  value={buttonId}
                  onChange={(e) => setButtonId(e.target.value)}
                >
                  <option value="">-- Choose a button --</option>
                  {buttons.map((b) => (
                    <option key={b} value={b}>
                      Button {b}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="rcm-filter-description">
              <small className="text-muted">{getFilterDescription()}</small>
            </div>
          </div>

          {/* Contacts Preview */}
          <div className="rcm-contacts-preview">
            <div className="rcm-preview-header">
              <div>
                <label className="form-label">
                  Contacts Preview ({previewCount} contacts)
                </label>
                {filter === "selected" && (
                  <div className="rcm-selection-actions">
                    <button
                      type="button"
                      className="rcm-btn-link"
                      onClick={selectAll}
                    >
                      Select All
                    </button>
                    <span>•</span>
                    <button
                      type="button"
                      className="rcm-btn-link"
                      onClick={deselectAll}
                    >
                      Deselect All
                    </button>
                    <span className="rcm-selected-count">
                      {selected.size} selected
                    </span>
                  </div>
                )}
              </div>

              {/* Search Box */}
              <div className="rcm-search-box">
                <BiSearch className="rcm-search-icon" />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search contacts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="rcm-contacts-table-container">
              {loading ? (
                <div className="rcm-loading-state">
                  <div className="rcm-spinner"></div>
                  <span>Loading contacts...</span>
                </div>
              ) : (
                <>
                  {filteredContacts.length === 0 ? (
                    <div className="rcm-empty-state">
                      <p>No contacts found matching your criteria</p>
                    </div>
                  ) : (
                    <>
                      <table className="rcm-contacts-table">
                        <thead>
                          <tr>
                            {filter === "selected" && <th width="50px"></th>}
                            <th>Mobile Number</th>
                            {buttons.map((b) => (
                              <th key={b} width="80px">
                                Btn {b}
                              </th>
                            ))}
                            <th width="80px">Total</th>
                            <th width="100px">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayedContacts.map((contact) => (
                            <tr key={contact.mobileNumber}>
                              {filter === "selected" && (
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={selected.has(contact.mobileNumber)}
                                    onChange={() => toggleSelect(contact.mobileNumber)}
                                    className="rcm-contact-checkbox"
                                  />
                                </td>
                              )}
                              <td className="rcm-mobile-number">
                                {contact.mobileNumber}
                              </td>
                              {buttons.map((b) => (
                                <td key={b} className="rcm-click-count">
                                  {contact.buttonClicks?.[b] || (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                              ))}
                              <td className="rcm-total-clicks">
                                {contact.totalClicks > 0 ? (
                                  <span className="rcm-badge rcm-badge-primary">
                                    {contact.totalClicks}
                                  </span>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td className="rcm-status">
                                {contact.totalClicks > 0 ? (
                                  <span className="rcm-status-clicked">
                                    <AiOutlineCheckCircle />
                                    Clicked
                                  </span>
                                ) : (
                                  <span className="rcm-status-not-clicked">
                                    <AiOutlineCloseCircle />
                                    Not Clicked
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {filteredContacts.length > 10 && (
                        <div className="rcm-expand-section">
                          <button
                            type="button"
                            className="rcm-btn-expand"
                            onClick={() => setExpandedContacts(!expandedContacts)}
                          >
                            {expandedContacts ? (
                              <>
                                <BiChevronUp />
                                Show Less
                              </>
                            ) : (
                              <>
                                <BiChevronDown />
                                Show All {filteredContacts.length} Contacts
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Send Options */}
          <div className="rcm-send-options">
            <label className="rcm-option-checkbox">
              <input
                type="checkbox"
                checked={immediate}
                onChange={(e) => setImmediate(e.target.checked)}
              />
              Send immediately
            </label>
            <small className="text-muted">
              {immediate
                ? "Campaign will be processed immediately by the system"
                : "Campaign will be scheduled for later processing"}
            </small>
          </div>
        </div>

        {/* Footer */}
        <div className="rcm-modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleResend}
            disabled={
              loading ||
              (filter === "selected" && selected.size === 0) ||
              (filter === "button" && !buttonId)
            }
          >
            {loading ? (
              <>
                <div className="rcm-spinner-small"></div>
                Creating...
              </>
            ) : (
              <>
                <BiSend className="me-1" />
                Create Resend Campaign ({previewCount})
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}